import React, { useState, useEffect } from "react";
import Chart from "react-apexcharts";
import useFetchProfile from "../../hooks/useFetchProfile";
import useFetchSubjects from "../../hooks/useFetchSubjects";
import jsPDF from "jspdf";
import "jspdf-autotable";
import logo from "../../assets/images/logo.png";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { LevelsSkillReportLoader } from "../../components/loader";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from "recharts";
import "./style.css";
import axios from "axios";

// Progress Bar Component
const ProgressBar = ({ percentage }) => {
  const getColor = (percentage) => {
    if (percentage >= 80) return "#458C41";
    if (percentage >= 60) return "#DB9646";
    if (percentage >= 40) return "#C8C11B";
    return "#EE3131";
  };

  const backgroundColor = (() => {
    if (percentage >= 80) return "#B5CE97";
    if (percentage >= 60) return "#CFB392";
    if (percentage >= 40) return "#DEDB9C";
    return "#DF8D8D";
  })();

  return (
    <div
      className="progress-bar"
      style={{
        backgroundColor,
      }}
    >
      <div
        className="progress-fill"
        style={{
          width: `${percentage}%`,
          backgroundColor: getColor(percentage),
        }}
      />
    </div>
  );
};

// Custom Bar Component
const CustomBar = (props) => {
  const { x, y, width, height, payload, highlightedSkill } = props;
  const isHighlighted = highlightedSkill === payload.skill;

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={payload.color}
        rx="8"
        ry="8"
        style={{
          filter: isHighlighted
            ? "drop-shadow(0 8px 12px rgba(255, 0, 0, 0.56))"
            : "none",
          transform: isHighlighted ? "translateY(-10px)" : "translateY(0)",
          transition: "all 0.3s ease",
        }}
      />
    </g>
  );
};

function InlineSVG({ url, topicsName }) {
  const [svgContent, setSvgContent] = useState(null);

  useEffect(() => {
    fetch(url)
      .then((res) => res.text())
      .then(setSvgContent)
      .catch((err) => console.error("Error loading SVG:", err));
  }, [url]);

  useEffect(() => {
    if (!svgContent) return;

    const container = document.getElementById(
      `inline-svg-container-${topicsName}`
    );
    if (!container) return;

    const allImages = container.querySelectorAll("image");
    if (!allImages.length) return;

    allImages.forEach((img) => {
      img.style.transition = "transform 0.5s ease-in-out";
      img.style.transformOrigin = "center";
      img.style.transformBox = "fill-box";
    });

    const handleMouseEnter = () => {
      allImages.forEach((img) => {
        img.setAttribute("transform", "translate(2,-5) scale(1.05)");
      });
    };

    const handleMouseLeave = () => {
      allImages.forEach((img) => {
        img.setAttribute("transform", "translate(0,0) scale(1)");
      });
    };

    container.addEventListener("mouseenter", handleMouseEnter);
    container.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      container.removeEventListener("mouseenter", handleMouseEnter);
      container.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [svgContent, topicsName]);

  if (!svgContent) return null;

  return (
    <div
      id={`inline-svg-container-${topicsName}`}
      className="topic-svg-container"
      dangerouslySetInnerHTML={{ __html: svgContent }}
    />
  );
}

// Overall Skills Static Data
function calculateOverallScore(skillArr) {
  const total = skillArr.reduce((sum, item) => sum + item.overall, 0);
  return Math.round(total / skillArr.length);
}

function countStrengthsAbove85(skillArr) {
  return skillArr.filter((s) => s.overall > 85).length;
}

function countAreasBelow70(skillArr) {
  return skillArr.filter((s) => s.overall < 70).length;
}

// const skillBreakdown = [
//   { skill: "Analytical Thinking", overall: 85 },
//   { skill: "Logical Reasoning", overall: 8 },
//   { skill: "Spatial Understanding", overall: 90 },
//   { skill: "Verbal Proficiency", overall: 72 },
//   { skill: "Quantitative Ability", overall: 88 },
//   { skill: "Problem Solving", overall: 82 },
//   { skill: "Critical Thinking", overall: 79 },
//   { skill: "Creativity", overall: 91 },
// ];

function getDerivedSkillBreakdown(levelSkillReport) {
  const skillMap = {};

  levelSkillReport.forEach((level) => {
    level.skills_report.forEach(({ skill, value }) => {
      if (!skillMap[skill]) {
        skillMap[skill] = { total: 0, count: 0 };
      }
      skillMap[skill].total += value;
      skillMap[skill].count += 1;
    });
  });

  return Object.entries(skillMap).map(([skill, data]) => ({
    skill,
    overall: Math.round(data.total / data.count),
  }));
}

// Color palette
const colorPalette = [
  "#a855f7",
  "#3b82f6",
  "#06b6d4",
  "#f97316",
  "#84cc16",
  "#eab308",
  "#c084fc",
  "#60a5fa",
];
const skillColorPalette = [
  "#a855f7",
  "#3b82f6",
  "#06b6d4",
  "#f97316",
  "#84cc16",
  "#eab308",
  "#c084fc",
  "#60a5fa",
  "#6b21a8",
  "#ec4899",
  "#10b981",
  "#78716c",
  "#fda4af",
  "#f87171",
  "#7ee8c4",
  "#facc15",
];

const CustomBarPrimary = (props) => {
  const { x, y, width, height, payload, colorKey } = props;
  const barColor = payload[colorKey] || "#a855f7";

  return (
    <rect
      x={x}
      y={y}
      width={width}
      height={height}
      fill={barColor}
      rx="8"
      ry="8"
      style={{
        transition: "all 0.3s ease",
      }}
    />
  );
};
const getBase64FromUrl = async (url) => {
  const res = await fetch(url);
  const blob = await res.blob();
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
};
function convertSkillObjectToArray(skillObj = {}) {
  return Object.entries(skillObj).map(([key, value]) => ({
    skill: key,
    value: Math.round(Number(value) || 0),
  }));
}
function SkillReportPage({ handleClick }) {
  const [profile] = useFetchProfile();
  const [subjects] = useFetchSubjects();

  // console.log("DEBUG → Subjects:", subjects);

  const [activeTab, setActiveTab] = useState("overall");
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [showGraph, setShowGraph] = useState(false);
  const [highlightedSkill, setHighlightedSkill] = useState(null);
  const [modalTab, setModalTab] = useState("skills");
  const [showSubjectModal, setShowSubjectModal] = useState(true);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [open, setOpen] = useState(false);
  const [isLoadingLevels, setIsLoadingLevels] = useState(false);
  const [subjectOverallMap, setSubjectOverallMap] = useState({});

  const { auth, setCommonWarning, breadcrumbs, setBreadcrumbs } = useAuth();
  const navigate = useNavigate();

  const [levelSkillReport, setLevelSkillReport] = useState([]);
  const [levels, setLevels] = useState([]);
  const [allSubjectReports, setAllSubjectReports] = useState([]);
  const [consistencyModal, setconsistencyModal] = useState(false);
  const [primarySkillModal, setPrimarySkillModal] = useState(false);
  // const [filteredConsistencyReport, setFilteredConsistencyReport] = useState(false);
  const [consistency_report, setConsistency_report] = useState([]);
  const [isLoadingModal, setIsLoadingModal] = useState(false);
  const [filteredSkillReport, setFilteredSkillReport] = useState([]);
  const [primarySkillLegend, setPrimarySkillLegend] = useState([]);
  const [strengthModal, setStrengthModal] = useState(false);
  const [improveModal, setImproveModal] = useState(false);

  useEffect(() => {
    if (showSubjectModal && subjects.length > 0) {
      fetchAllSubjectReports();
    }
  }, [showSubjectModal, subjects]);

  const fetchAllSubjectReports = async () => {
    const subjectReportsArray = [];
    // const allConsistencyData = [];

    for (const subject of subjects) {
      const reports = await fetchOverallReportsBySubject(subject.id);

      if (reports) {
        subjectReportsArray.push({
          subjectId: subject.id,
          subjectName: subject.name,
          reports: reports,
        });
        // allConsistencyData.push(...reports);
      }
    }

    setAllSubjectReports(subjectReportsArray);
    // setConsistency_report(allConsistencyData);
    // console.log("All Subject Reports:", subjectReportsArray);
  };

  const fetchOverallReportsBySubject = async (subject) => {
    // console.log("subject id", subject)
    try {
      if (!subject) {
        setCommonWarning("Subject not found");
        return;
      }

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}overallreportbysubject`,
        {
          token: auth.token,
          subject: subject,
        }
      );
      // console.log(res);
      if (res.status === 200) {
        // console.log("Overall Report by Subject:", res.data.data);
        const dataArray = res.data.data;
        setLevelSkillReport(dataArray);
        setLevels(dataArray);

        // Transform data and calculate consistency percent from topics
        const validConsistencyData = dataArray
          .filter((item) => item.topics && item.topics.length > 0)
          .map((item) => {
            // Calculate percent from topic_percentage values using consistency formula
            const topicPercentages = item.topics
              .map((topic) => topic.topic_percentage)
              .filter(
                (percent) => percent !== null && !isNaN(percent) && percent > 0
              );

            let calculatedPercent = 0;

            if (topicPercentages.length > 0) {
              // Use the consistency formula on topic percentages
              if (topicPercentages.length === 1) {
                calculatedPercent = topicPercentages[0];
              } else {
                // Calculate mean
                const mean =
                  topicPercentages.reduce((sum, val) => sum + val, 0) /
                  topicPercentages.length;

                // Calculate standard deviation
                const squaredDifferences = topicPercentages.map((val) =>
                  Math.pow(val - mean, 2)
                );
                const variance =
                  squaredDifferences.reduce((sum, val) => sum + val, 0) /
                  topicPercentages.length;
                const standardDeviation = Math.sqrt(variance);

                // Calculate coefficient of variation (CV)
                const cv = (standardDeviation / mean) * 100;

                // Calculate consistency: 100 - CV
                calculatedPercent = 100 - cv;

                // Round and clamp
                calculatedPercent = Math.round(calculatedPercent * 100) / 100;
                calculatedPercent = Math.max(
                  0,
                  Math.min(100, calculatedPercent)
                );
              }
            }

            return {
              level: item.name,
              percent: calculatedPercent,
              // Optional: include topic percentages for debugging
              topic_percentages: topicPercentages,
            };
          })
          .filter((item) => item.percent > 0); // Only include items with calculated percent > 0
        // console.log("Valid Consistency Data:", validConsistencyData);

        // Set consistency report for THIS selected subject only
        setConsistency_report(validConsistencyData);

        const filteredSkillReport = dataArray
          .filter((item) => item.skills_report && item.skills_report.length > 0)
          .map((item) => {
            const highestSkillEntry = item.skills_report.reduce((max, curr) => {
              return curr.value > max.value ? curr : max;
            }, item.skills_report[0]);
            return {
              level: item.name,
              highest_skill: highestSkillEntry.value,
              skills: item.skills_report
                .map((skill) =>
                  skill.value === highestSkillEntry.value ? skill.skill : null
                )
                .filter(Boolean),
            };
          });
        // console.log("Filtered Skill Report:", filteredSkillReport);
        setFilteredSkillReport(filteredSkillReport);
        const primarySkillLegend = Array.from(
          new Set(filteredSkillReport.flatMap((item) => item.skills || []))
        );
        setPrimarySkillLegend(primarySkillLegend);

        return dataArray;
      } else if (res.status === 400) {
        setCommonWarning(res.message);
      } else if (res.status === 401) {
        navigate("/login");
        return [];
      }
    } catch (error) {
      console.error("Reports API ERROR", error);
      setCommonWarning("Failed to load reports");
    }
  };

  const filteredConsistencyReport = consistency_report.filter((item) => {
    // Check which field exists in your data
    const value = item.percent !== undefined ? item.percent : item.percentage;
    return value > 0;
  });

  function generateChartData(levelSkillReport) {
    // Handle case when levelSkillReport might be empty or undefined
    if (!levelSkillReport || !Array.isArray(levelSkillReport)) {
      return [];
    }

    const skillMap = {};

    levelSkillReport.forEach((level) => {
      // Make sure level has skills_report
      if (level && level.skills_report && Array.isArray(level.skills_report)) {
        level.skills_report.forEach(({ skill, value }) => {
          if (!skillMap[skill]) {
            skillMap[skill] = { total: 0, count: 0 };
          }

          skillMap[skill].total += value;
          skillMap[skill].count += 1;
        });
      }
    });

    return Object.entries(skillMap).map(([skill, data]) => ({
      skill,
      value: Math.round(data.total / data.count),
    }));
  }

  useEffect(() => {
    if (allSubjectReports.length > 0) {
      // Combine all level reports from all subjects
      const allLevelReports = allSubjectReports.flatMap((subject) =>
        subject.reports.filter((item) => item.level_report)
      );
      setLevelSkillReport(allLevelReports);
    }
  }, [allSubjectReports]);

  const chartData = generateChartData(levelSkillReport);
  // console.log("Chart Data:", chartData);

  const derivedSkillBreakdown = getDerivedSkillBreakdown(levelSkillReport);

  const topStrengthSkills = derivedSkillBreakdown.filter((s) => s.overall > 85);

  const improvementSkills = derivedSkillBreakdown.filter((s) => s.overall < 70);

  function calculateConsistencyAverage(consistencyData) {
    try {
      if (
        !consistencyData ||
        !Array.isArray(consistencyData) ||
        consistencyData.length === 0
      ) {
        return 0;
      }

      // For your data structure: consistencyData contains level reports
      // Each level has a percentage field
      const percentages = consistencyData
        .map((item) => {
          // Check both possible field names
          const value =
            item.percent !== undefined ? item.percent : item.percentage;

          // Handle null values - convert to number or skip
          if (value === null || value === undefined) {
            return null;
          }

          const numValue =
            typeof value === "number" ? value : parseFloat(value);
          return !isNaN(numValue) && numValue > 0 ? numValue : null;
        })
        .filter((value) => value !== null && value > 0);

      // console.log("Consistency calculation - Valid percentages:", percentages);

      if (percentages.length === 0) {
        // console.log("No valid percentages found");
        return 0;
      }

      if (percentages.length === 1) {
        // console.log("Only one valid percentage, consistency set to 100");
        return 100;
      }

      // Calculate mean
      const mean =
        percentages.reduce((sum, val) => sum + val, 0) / percentages.length;
      // console.log("Mean:", mean);

      // Calculate standard deviation
      const squaredDifferences = percentages.map((val) =>
        Math.pow(val - mean, 2)
      );
      const variance =
        squaredDifferences.reduce((sum, val) => sum + val, 0) /
        percentages.length;
      const standardDeviation = Math.sqrt(variance);
      // console.log("Standard Deviation:", standardDeviation);

      // Calculate coefficient of variation (CV)
      const cv = (standardDeviation / mean) * 100;
      // console.log("Coefficient of Variation (CV):", cv, "%");

      // Calculate consistency: 100 - CV
      let consistency = 100 - cv;
      // console.log("Raw Consistency:", consistency, "%");

      // Round and clamp between 0 and 100
      consistency = Math.round(consistency * 100) / 100; // Keep 2 decimal places
      consistency = Math.max(0, Math.min(100, consistency));

      // console.log("Final Consistency:", consistency, "%");

      return consistency;
    } catch (error) {
      console.error("Error calculating consistency:", error);
      return 0;
    }
  }

  const overallSkillsData = {
    overview: {
      totalScore: calculateOverallScore(derivedSkillBreakdown),
      strengths: countStrengthsAbove85(derivedSkillBreakdown),
      areasToImprove: countAreasBelow70(derivedSkillBreakdown),
      consistency: calculateConsistencyAverage(filteredConsistencyReport),
    },
  };
  // Add debugging logs
  // console.log("========== DEBUGGING CONSISTENCY ==========");
  // console.log("1. consistency_report:", consistency_report);
  // console.log("2. Filtered consistency data:", filteredConsistencyReport);
  // console.log("3. Chart data (might be empty):", chartData);
  // console.log("4. Derived skill breakdown:", derivedSkillBreakdown);
  // console.log("5. Level skill report:", levelSkillReport);
  // console.log("6. Calculated consistency:", overallSkillsData.overview.consistency);
  // console.log("7. All subject reports:", allSubjectReports);
  // console.log("===========================================");

  const getColorForSkill = (index) => {
    return colorPalette[index % colorPalette.length];
  };

  const chartDataWithColors = chartData.map((item, idx) => ({
    ...item,
    color: getColorForSkill(idx),
  }));

  const radarData = chartDataWithColors.map((item) => ({
    name: item.skill,
    value: item.value,
  }));

  const handleViewSkills = (levelId) => {
    setIsLoadingModal(true);
    setSelectedLevel(levelId);
    setShowGraph(true);

    setTimeout(() => {
      setIsLoadingModal(false);
    }, 1500);
  };

  const closeGraph = () => {
    setShowGraph(false);
    setSelectedLevel(null);
    setModalTab("skills");
  };

  const handleNextLevel = () => {
    const unlockedLevels = levels.filter((l) => l.percentage > 0);

    const currentIndex = unlockedLevels.findIndex(
      (l) => l.id === selectedLevel
    );
    if (currentIndex < unlockedLevels.length - 1) {
      setSelectedLevel(unlockedLevels[currentIndex + 1].id);
    }
  };

  const handlePrevLevel = () => {
    const unlockedLevels = levels.filter((l) => l.percentage > 0);
    const currentIndex = unlockedLevels.findIndex(
      (l) => l.id === selectedLevel
    );
    if (currentIndex > 0) {
      setSelectedLevel(unlockedLevels[currentIndex - 1].id);
    }
  };

  const getNavigationState = () => {
    const unlockedLevels = levels.filter((l) => l.percentage > 0);
    const currentIndex = unlockedLevels.findIndex(
      (l) => l.id === selectedLevel
    );
    return {
      canPrev: currentIndex > 0,
      canNext: currentIndex < unlockedLevels.length - 1,
    };
  };

  const maxValue = Math.max(...chartDataWithColors.map((s) => s.value), 0);
  const maxSkills = chartDataWithColors.filter(
    (skill) => skill.value === maxValue
  );
  const maxSkill = maxSkills[0] || {
    skill: "N/A",
    value: 0,
    color: "#000000",
  };

  const navState = getNavigationState();

  const handleDownloadReport = async () => {
    if (!profile || profile.length === 0) {
      alert("Profile is still loading. Please try again in 1 second.");
      return;
    }

    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let y = 0;
    const margin = 20;
    const contentWidth = pageWidth - 2 * margin;

    const user = profile[0];
    const baseURL = "http://localhost:5001/";

    const hexToRgb = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? [
            parseInt(result[1], 16),
            parseInt(result[2], 16),
            parseInt(result[3], 16),
          ]
        : [0, 0, 0];
    };

    const platformLogo = await getBase64FromUrl(logo);

    // ========== PAGE 1: COVER ==========
    doc.setFillColor(168, 85, 247);
    doc.rect(0, 0, pageWidth, pageHeight * 0.5, "F");

    // Logo - BIGGER
    doc.addImage(platformLogo, "PNG", margin, 10, 45, 35);

    // Brand name
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(15);
    doc.setFont("helvetica", "bold");
    doc.text("FefdyBraingym", margin + 50, 25);

    doc.setFontSize(13);
    doc.setFont("helvetica", "normal");
    doc.text("www.fefdybraingym.com", margin + 50, 31);

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(42);
    doc.setFont("helvetica", "bold");
    doc.text("Skill Assessment", margin, 75);
    doc.text("Report", margin, 100);

    doc.setFontSize(18);
    doc.setFont("helvetica", "normal");
    doc.text("Your Personal Learning Profile", margin, 112);
    doc.text(`Subject: ${selectedSubject?.name || "General"}`, margin, 125);

    // Bottom section
    doc.setFillColor(255, 255, 255);
    doc.rect(0, pageHeight * 0.5, pageWidth, pageHeight * 0.5, "F");

    doc.setTextColor(50, 50, 50);
    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.text(`${user.name}`, margin, pageHeight * 0.55 + 20);

    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(120, 120, 120);
    doc.text(`${user.email}`, margin, pageHeight * 0.55 + 35);
    doc.text(
      `Generated: ${new Date().toLocaleDateString()}`,
      margin,
      pageHeight * 0.55 + 45
    );

    doc.setDrawColor(168, 85, 247);
    doc.setLineWidth(0.5);
    doc.line(
      margin,
      pageHeight * 0.55 + 55,
      margin + 40,
      pageHeight * 0.55 + 55
    );

    // ========== PAGE 2: OVERVIEW ==========
    doc.addPage();
    y = margin;

    // Header
    doc.setTextColor(168, 85, 247);
    doc.setFontSize(28);
    doc.setFont("helvetica", "bold");
    doc.text("Executive Summary", margin, y);
    y += 12;

    doc.setDrawColor(168, 85, 247);
    doc.setLineWidth(2);
    doc.line(margin, y, margin + 50, y);
    y += 15;

    console.log("Overall Skills Data:", overallSkillsData);
    const stats = [
      {
        label: "Overall Score",
        value: `${overallSkillsData.overview.totalScore}/100`,
        color: [168, 85, 247],
      },
      {
        label: "Top Strengths",
        value: overallSkillsData.overview.strengths,
        color: [59, 130, 246],
      },
      {
        label: "Areas to Improve",
        value: overallSkillsData.overview.areasToImprove,
        color: [249, 115, 22],
      },
      {
        label: "Consistency",
        value: `${overallSkillsData.overview.consistency}%`,
        color: [34, 197, 94],
      },
    ];

    const cardWidth = (contentWidth - 8) / 2;
    const cardHeight = 35;
    let cardX = margin;
    let cardY = y;

    stats.forEach((stat, idx) => {
      if (idx === 2) {
        cardX = margin;
        cardY += cardHeight + 8;
      } else if (idx === 1) {
        cardX = margin + cardWidth + 8;
      }

      // Card background
      doc.setFillColor(245, 245, 250);
      doc.roundedRect(cardX, cardY, cardWidth, cardHeight, 3, 3, "F");

      // Color accent
      doc.setFillColor(...stat.color);
      doc.rect(cardX, cardY, 4, cardHeight, "F");

      // Text
      doc.setTextColor(100, 100, 100);
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text(stat.label, cardX + 8, cardY + 10);

      doc.setTextColor(...stat.color);
      doc.setFontSize(20);
      doc.setFont("helvetica", "bold");
      doc.text(stat.value.toString(), cardX + 8, cardY + 22);
    });

    y = cardY + cardHeight + 20;

    // Section: Skills Overview
    doc.setTextColor(168, 85, 247);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Top 8 Skills", margin, y);
    y += 10;

    const topSkills = chartDataWithColors.slice(0, 8);
    topSkills.forEach((skill, idx) => {
      const barLength = (contentWidth - 45) * (skill.value / 100);

      // Skill name
      doc.setTextColor(50, 50, 50);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.text(skill.skill, margin, y);

      // Progress bar background
      doc.setFillColor(240, 240, 245);
      doc.rect(margin + 65, y - 3, contentWidth - 75, 5, "F");

      // Progress bar fill
      doc.setFillColor(
        ...skill.color.match(/\w\w/g).map((x) => parseInt(x, 16))
      );
      doc.rect(margin + 65, y - 3, barLength, 5, "F");

      y += 8;
    });

    // ========== PAGE 3: DETAILED SKILLS ==========
    doc.addPage();
    y = margin;

    doc.setTextColor(168, 85, 247);
    doc.setFontSize(28);
    doc.setFont("helvetica", "bold");
    doc.text("Detailed Skill Breakdown", margin, y);
    y += 12;

    doc.setDrawColor(168, 85, 247);
    doc.setLineWidth(2);
    doc.line(margin, y, margin + 50, y);
    y += 15;

    // Skills table
    const skillRows = chartDataWithColors.map((s) => ({
      Skill: s.skill,
      Score: s.value,
      Rating:
        s.value >= 90
          ? "Excellent"
          : s.value >= 80
          ? "Strong"
          : s.value >= 70
          ? "Moderate"
          : "Needs Improvement",
    }));

    doc.autoTable({
      startY: y,
      head: [["Skill", "Score", "Rating"]],
      body: skillRows.map((r) => [r.Skill, r.Score, r.Rating]),
      theme: "striped",
      headStyles: {
        fillColor: [168, 85, 247],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        fontSize: 12,
        padding: 8,
      },
      bodyStyles: {
        fontSize: 11,
        padding: 7,
        textColor: [50, 50, 50],
      },
      alternateRowStyles: {
        fillColor: [245, 245, 250],
      },
      columnStyles: {
        1: { halign: "center" },
        2: { halign: "center" },
      },
      margin: margin,
    });

    y = doc.lastAutoTable.finalY + 15;

    // Strengths section - WITH ICONS
    doc.setTextColor(34, 197, 94);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Top Strengths", margin, y);
    y += 8;

    doc.setFontSize(11);
    doc.setTextColor(50, 50, 50);
    doc.setFont("helvetica", "normal");
    // console.log("Chart Data for Strengths:", chartDataWithColors);
    const strengths = chartDataWithColors
      .filter((Item) => Item.value > 85);
    // console.log("Identified Strengths:", strengths);
    strengths.forEach((s) => {
      doc.text(`• ${s.skill} — ${s.value}/100`, margin + 5, y);
      y += 6;
    });

    y += 5;

    // Growth areas section - WITH ICONS
    doc.setTextColor(249, 115, 22);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Areas for Growth", margin, y);
    y += 8;

    doc.setFontSize(11);
    doc.setTextColor(50, 50, 50);
    doc.setFont("helvetica", "normal");
    const weak = chartDataWithColors
      .filter((Item) => Item.value < 70);
    weak.forEach((s) => {
      doc.text(`• ${s.skill} — ${s.value}/100`, margin + 5, y);
      y += 6;
    });

    // ========== PAGE 4: CLOSING ==========
    doc.addPage();
    y = margin;

    // Centered closing section
    doc.setTextColor(168, 85, 247);
    doc.setFontSize(24);
    doc.setFont("helvetica", "bold");
    doc.text("Thank You!", pageWidth / 2, pageHeight / 2 - 30, {
      align: "center",
    });

    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80, 80, 80);
    doc.text(
      "Keep practicing and watch your skills grow!",
      pageWidth / 2,
      pageHeight / 2,
      { align: "center" }
    );

    // Brand footer
    doc.setFillColor(245, 245, 250);
    doc.rect(0, pageHeight - 40, pageWidth, 40, "F");

    doc.setTextColor(50, 50, 50);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("FefdyBraingym", pageWidth / 2, pageHeight - 25, {
      align: "center",
    });

    doc.setTextColor(120, 120, 120);
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text("www.fefdybraingym.com", pageWidth / 2, pageHeight - 15, {
      align: "center",
    });

    // ========== FOOTER ON ALL PAGES ==========
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);

      // Footer background
      doc.setFillColor(245, 245, 250);
      doc.rect(0, pageHeight - 12, pageWidth, 12, "F");

      // Footer text
      doc.setFontSize(9);
      doc.setTextColor(150, 150, 150);
      doc.setFont("helvetica", "normal");
      doc.text(
        `© 2025 FefdyBraingym • www.fefdybraingym.com`,
        margin,
        pageHeight - 6
      );
      doc.text(
        `Page ${i} of ${totalPages}`,
        pageWidth - margin - 20,
        pageHeight - 6
      );
    }

    // Save
    doc.save(`Skill-Assessment-Report-${user.name}.pdf`);
  };

  return (
    <>
      {/* SUBJECT MODAL */}
      {showSubjectModal && (
        <div className="graph-overlay" onClick={() => setShowSubjectModal(false)}>
          <div className="graph-sub-modal-box" onClick={(e) => e.stopPropagation()}>
            <h2 className="subject-modal-title">Select a Subject</h2>

            {allSubjectReports.length === 0 ? (
              <LevelsSkillReportLoader />
            ) : (
              <div className="subject-list">
                {allSubjectReports.map((sub) => (
                  <div key={sub.subjectId} className="subject-card">
                    {/* SUBJECT BUTTON */}
                    <button
                      className="subject-select-btn"
                      onClick={() => {
                        const subjectData = allSubjectReports.find(
                          (s) => s.subjectId === sub.subjectId
                        );

                        if (subjectData) {
                          setSelectedSubject(sub);
                          setLevels(subjectData.reports);
                          setLevelSkillReport(subjectData.reports);
                          setShowSubjectModal(false);
                          fetchOverallReportsBySubject(sub.subjectId);
                        }
                      }}
                    >
                      {sub.subjectName}
                    </button>

                    {/* LEVEL LIST - USE sub.reports INSTEAD OF levels */}
                    <div className="levels-list">
                      {sub.reports
                        .filter((level) => level.percentage > 0)
                        .map((level) => {
                          const progress = level.percentage ?? 0;
                          const levelName = level.name ?? `Level ${level.id}`;

                          return (
                            <div key={level.id} className="level-item-card">
                              <h2 className="level-item-title">{levelName}</h2>

                              <div className="level-item-progress">
                                <div className="level-item-progress-bar">
                                  <div
                                    className="level-item-progress-fill"
                                    style={{ width: `${progress}%` }}
                                  ></div>
                                </div>

                                <p className="level-item-progress-text">
                                  {progress}% complete
                                </p>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* MAIN PAGE CONTENT – ONLY SHOW AFTER SUBJECT SELECT */}

      <div className={`skill-report-page ${showSubjectModal ? "blurred" : ""}`}>
        <div className="tabs-wrapper">
          {selectedSubject && (
            <div className="subject-label">
              <div
                className="custom-select"
                onClick={() => {
                  setOpen(!open);
                }}
              >
                <div className="custom-selected">
                  {selectedSubject?.name || "Select Subject"}
                </div>

                {open && (
                  <ul className="custom-options">
                    {subjects.map((s) => (
                      <li
                        key={s.id}
                        className="custom-option"
                        onClick={() => {
                          const subjectData = allSubjectReports.find(
                            (subj) => subj.subjectId === s.id
                          );

                          if (subjectData) {
                            setIsLoadingLevels(true);

                            setTimeout(() => {
                              setSelectedSubject(s);
                              setLevels(subjectData.reports);
                              setLevelSkillReport(subjectData.reports);
                              setOpen(false);
                              setIsLoadingLevels(false);
                              fetchOverallReportsBySubject(s.id);
                            }, 800);
                          }
                        }}
                      >
                        {s.name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          <div className="tabs-container">
            <button
              className={`tab ${activeTab === "overall" ? "active" : ""}`}
              onClick={() => setActiveTab("overall")}
            >
              Overall Skills
            </button>

            <button
              className={`tab ${activeTab === "levelwise" ? "active" : ""}`}
              onClick={() => setActiveTab("levelwise")}
            >
              Level Wise Skills
            </button>

            <button
              className={`tab ${activeTab === "consistency" ? "active" : ""}`}
              onClick={() => setActiveTab("consistency")}
            >
              Consistency
            </button>
          </div>
        </div>

        {isLoadingLevels ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "500px",
            }}
          >
            <LevelsSkillReportLoader />
          </div>
        ) : (
          <>
            {activeTab === "overall" && (
              <div className="overall-skills-section">
                <div className="overall-header">
                  <div className="stat-card stat-card-1">
                    <div className="stat-icon">
                      <svg
                        width="32"
                        height="32"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"></path>
                        <path d="M21 3v5h-5"></path>
                        <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"></path>
                        <path d="M3 21v-5h5"></path>
                      </svg>
                    </div>
                    <div className="stat-content">
                      <p className="stat-label">Overall Score</p>
                      <p className="stat-value">
                        {overallSkillsData.overview.totalScore}/100
                      </p>
                    </div>
                  </div>

                  <div
                    className="stat-card stat-card-2"
                    style={{ cursor: "pointer" }}
                    onClick={() => setStrengthModal(true)}
                  >
                    <div className="stat-icon">
                      <svg
                        width="32"
                        height="32"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path>
                        <polyline points="13 2 13 9 20 9"></polyline>
                        <path d="M11 15h2"></path>
                        <path d="M11 19h2"></path>
                      </svg>
                    </div>
                    <div className="stat-content">
                      <p className="stat-label">Top Strengths</p>
                      <p className="stat-value">
                        {overallSkillsData.overview.strengths}
                      </p>
                    </div>
                  </div>

                  <div
                    className="stat-card stat-card-3"
                    style={{ cursor: "pointer" }}
                    onClick={() => setImproveModal(true)}
                  >
                    <div className="stat-icon">
                      <svg
                        width="32"
                        height="32"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <circle cx="12" cy="12" r="10"></circle>
                        <path d="M12 6v6l4 2"></path>
                      </svg>
                    </div>
                    <div className="stat-content">
                      <p className="stat-label">Areas to Improve</p>
                      <p className="stat-value">
                        {overallSkillsData.overview.areasToImprove}
                      </p>
                    </div>
                  </div>

                  <div
                    className="stat-card stat-card-6"
                    onClick={() => setPrimarySkillModal(true)}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="stat-icon">
                      <svg
                        width="32"
                        height="32"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M12 2L15.09 8.26H22L17.55 12.5L19.64 18.76L12 14.01L4.36 18.76L6.45 12.5L2 8.26H8.91L12 2Z"></path>
                      </svg>
                    </div>
                    <div className="stat-content">
                      <p className="stat-label">Top Primary Skill</p>
                      <div className="primary-skill-display">
                        {primarySkillLegend.length > 0 ? (
                          <div className="primary-skills-mini">
                            {primarySkillLegend
                              .slice(0, 1)
                              .map((skill, idx) => (
                                <span
                                  key={idx}
                                  className="skill-badge"
                                  style={{
                                    backgroundColor:
                                      skillColorPalette[
                                        idx % skillColorPalette.length
                                      ],
                                    color: "white",
                                  }}
                                >
                                  {skill}
                                </span>
                              ))}
                            {primarySkillLegend.length > 2 && (
                              <span className="skill-badge-more">
                                +{primarySkillLegend.length - 2}
                              </span>
                            )}
                          </div>
                        ) : (
                          <p className="stat-value">N/A</p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div
                    className="stat-card stat-card-5"
                    onClick={handleDownloadReport}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="stat-icon">
                      <svg
                        width="32"
                        height="32"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="7 10 12 15 17 10"></polyline>
                        <line x1="12" y1="15" x2="12" y2="3"></line>
                      </svg>
                    </div>
                    <div className="stat-content">
                      <p className="stat-label">Download Report</p>
                      <p className="download-btn">PDF</p>
                    </div>
                  </div>
                </div>

                <div className="overall-content">
                  <div className="overall-radar-section">
                    <h3 className="section-title">All Skills</h3>
                    <div className="radar-container">
                      <ResponsiveContainer width="100%" height={400}>
                        <RadarChart data={radarData}>
                          <PolarGrid stroke="#e0e0e0" />
                          <PolarAngleAxis
                            dataKey="name"
                            tick={{ fontSize: 11 }}
                          />
                          <PolarRadiusAxis angle={90} domain={[0, 100]} />
                          <Radar
                            name="Score"
                            dataKey="value"
                            stroke="#a855f7"
                            fill="#a855f7"
                            fillOpacity={0.4}
                          />
                        </RadarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div className="overall-breakdown-section">
                    <div>
                      <h3 className="section-title">Skills Overview</h3>
                    </div>

                    <div className="skills-list">
                      {chartDataWithColors.map((skill, idx) => (
                        <div key={idx} className="skill-item">
                          <div className="skill-name-row">
                            <div
                              className="skill-dot"
                              style={{ backgroundColor: skill.color }}
                            ></div>
                            <span className="skill-name">{skill.skill}</span>
                            <span className="skill-overall-score">
                              {skill.value}
                            </span>
                          </div>
                          <div className="skill-progress-bar">
                            <div
                              className="skill-progress-fill"
                              style={{
                                width: `${skill.value}%`,
                                backgroundColor: skill.color,
                              }}
                            ></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "levelwise" && (
              <div className="levelwise-report">
                <div className="rep-levels-grid">
                  {levels.map((level) => {
                    const progress = level.percentage ?? 0;
                    const levelName = level.name ?? `Level ${level.id}`;
                    const isLocked = progress === 0;

                    return (
                      <div key={level.id} className="rep-levels-card">
                        <div className="fun-shapes">
                          <div className="shape shape-1"></div>
                        </div>

                        <h2 className="levels-rep-title">{levelName}</h2>

                        {isLocked && (
                          <div className="rep-locked-section">
                            <div className="rep-lock-icon">
                              <svg
                                width="30"
                                height="30"
                                viewBox="0 0 24 24"
                                stroke="#444"
                                fill="none"
                                strokeWidth="2"
                              >
                                <rect
                                  x="3"
                                  y="11"
                                  width="18"
                                  height="11"
                                  rx="2"
                                  ry="2"
                                ></rect>
                                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                              </svg>
                            </div>
                            <p className="rep-locked-text">
                              Complete this level to unlock your report
                            </p>
                          </div>
                        )}

                        {!isLocked && (
                          <>
                            <div className="report-progress-div">
                              <div className="rep-progress-bar">
                                <div
                                  className="rep-progress-fill"
                                  style={{ width: `${progress}%` }}
                                ></div>
                              </div>
                              <p className="rep-progress-text">
                                {progress}% complete
                              </p>
                            </div>

                            <button
                              className="rep-btn"
                              onClick={() => handleViewSkills(level.id)}
                            >
                              View your skills
                            </button>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {activeTab === "consistency" && (
              <div className="consistency-report">
                <div
                  className="c-graph-modal-box"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="modal-header">
                    <h2>
                      Your Learning Consistency for the subject :{" "}
                      <span className="consistency-sub-name">
                        {selectedSubject?.name}
                      </span>
                    </h2>
                  </div>

                  <div className="consistency-chart">
                    <ResponsiveContainer width="100%" height={400}>
                      <BarChart data={filteredConsistencyReport}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="level" />
                        <YAxis domain={[0, 100]} />
                        <Tooltip
                          cursor={false}
                          position={{ y: 0 }}
                          contentStyle={{
                            backgroundColor: "#ffffff",
                            borderRadius: "8px",
                            border: "1px solid #e5e7eb",
                            fontSize: "16px",
                          }}
                          formatter={(value) => [`${value}%`, "Consistency"]}
                        />
                        <Bar
                          dataKey="percent"
                          fill="#a855f7"
                          radius={[12, 12, 0, 0]}
                          barSize={38}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
        {showGraph && selectedLevel && (
          <div className="graph-overlay" onClick={closeGraph}>
            <div
              className="graph-modal-box"
              onClick={(e) => e.stopPropagation()}
            >
              {isLoadingModal ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "500px",
                    width: "100%",
                  }}
                >
                  <LevelsSkillReportLoader />
                </div>
              ) : (
                <>
                  <button className="graph-close-btn" onClick={closeGraph}>
                    ×
                  </button>

                  {/* Modal Tabs */}
                  <div className="modal-tabs-container">
                    <button
                      className={`modal-tab ${
                        modalTab === "skills" ? "active" : ""
                      }`}
                      onClick={() => setModalTab("skills")}
                    >
                      Skills
                    </button>
                    <button
                      className={`modal-tab ${
                        modalTab === "topics" ? "active" : ""
                      }`}
                      onClick={() => setModalTab("topics")}
                    >
                      Topics
                    </button>
                  </div>

                  {/* Skills Tab Content */}
                  {modalTab === "skills" &&
                    (() => {
                      const selectedLevelData = levels.find(
                        (l) => l.id === selectedLevel
                      );
                      const levelSkills =
                        selectedLevelData?.skills_report || [];
                      const levelSkillsWithColors = levelSkills.map(
                        (skill, idx) => ({
                          skill: skill.skill,
                          value: skill.value,
                          color: getColorForSkill(idx),
                        })
                      );
                      const levelMaxValue = Math.max(
                        ...levelSkillsWithColors.map((s) => s.value),
                        0
                      );
                      const levelMaxSkills = levelSkillsWithColors.filter(
                        (skill) => skill.value === levelMaxValue
                      );
                      const levelMaxSkill = levelMaxSkills[0] || {
                        skill: "N/A",
                        value: 0,
                        color: "#000000",
                      };

                      return (
                        <div className="graph-wrapper">
                          <div className="gauge-section">
                            <div className="gauge-text">
                              {levelMaxSkills.length === 1 ? (
                                <>
                                  <p>You're excelling in</p>
                                  <span
                                    className="skill-highlight"
                                    style={{ color: levelMaxSkill.color }}
                                  >
                                    {levelMaxSkill.skill}
                                  </span>
                                </>
                              ) : (
                                <>
                                  <p>Your Highest-Scoring Skills</p>
                                  <div className="skill-list">
                                    {levelMaxSkills.map((skill, idx) => (
                                      <p
                                        key={idx}
                                        style={{ color: skill.color }}
                                      >
                                        • {skill.skill}
                                      </p>
                                    ))}
                                  </div>
                                </>
                              )}
                            </div>

                            <div style={{ width: "100%" }}>
                              <Chart
                                options={{
                                  chart: {
                                    type: "radialBar",
                                    sparkline: { enabled: true },
                                    width: 1000,
                                  },
                                  plotOptions: {
                                    radialBar: {
                                      startAngle: -90,
                                      endAngle: 90,
                                      track: {
                                        background: "#e7e7e7",
                                        strokeWidth: "85%",
                                        margin: 5,
                                      },
                                      hollow: {
                                        margin: 3,
                                        size: "60%",
                                      },
                                      dataLabels: {
                                        name: { show: false },
                                        value: {
                                          offsetY: 0,
                                          fontSize: "42px",
                                          fontWeight: "800",
                                          fontFamily: "Alata, sans-serif",
                                          color: "#000000ff",
                                        },
                                      },
                                    },
                                  },
                                  colors: [
                                    levelMaxSkills.length > 1
                                      ? "#f59e0b"
                                      : levelMaxSkill.color,
                                  ],
                                  stroke: {
                                    lineCap: "round",
                                  },
                                }}
                                series={[levelMaxSkill.value]}
                                type="radialBar"
                                height="350"
                              />
                            </div>
                          </div>

                          <div className="bar-section">
                            <div className="bar-header">
                              <p className="bar-subtitle">
                                Performance Metrics
                              </p>
                              <h3 className="bar-title">
                                Skills Assessment
                                <span className="level-badge">
                                  {selectedLevelData?.name}
                                </span>
                              </h3>
                            </div>

                            <div className="graph-chart">
                              <ResponsiveContainer width="100%" height={320}>
                                <BarChart
                                  data={levelSkillsWithColors}
                                  margin={{
                                    top: 20,
                                    right: 30,
                                    left: 0,
                                    bottom: 10,
                                  }}
                                >
                                  <CartesianGrid
                                    strokeDasharray="3 3"
                                    stroke="#e0e0e0"
                                    vertical={false}
                                  />
                                  <XAxis
                                    dataKey="skill"
                                    tick={false}
                                    axisLine={false}
                                  />
                                  <YAxis
                                    domain={[0, 100]}
                                    tick={{ fontSize: 12 }}
                                    ticks={[0, 20, 40, 60, 80, 100]}
                                  />
                                  <Tooltip
                                    contentStyle={{
                                      backgroundColor: "#fafafaff",
                                      border: "1px solid #e0e0e0",
                                      borderRadius: "8px",
                                      padding: "12px",
                                    }}
                                  />
                                  <Bar
                                    dataKey="value"
                                    shape={
                                      <CustomBar
                                        highlightedSkill={highlightedSkill}
                                      />
                                    }
                                    radius={[12, 12, 0, 0]}
                                  />
                                </BarChart>
                              </ResponsiveContainer>
                            </div>

                            <div className="bar-legend">
                              {levelSkillsWithColors.map((item, idx) => (
                                <div
                                  key={idx}
                                  className="legend-dot"
                                  style={{
                                    color: item.color,
                                  }}
                                  onMouseEnter={() =>
                                    setHighlightedSkill(item.skill)
                                  }
                                  onMouseLeave={() => setHighlightedSkill(null)}
                                  onClick={() =>
                                    setHighlightedSkill(
                                      highlightedSkill === item.skill
                                        ? null
                                        : item.skill
                                    )
                                  }
                                >
                                  <div
                                    className="legend-dot-circle"
                                    style={{
                                      backgroundColor: item.color,
                                    }}
                                  ></div>
                                  {item.skill}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  {/* Subtopic Wise Tab Content */}
                  {modalTab === "topics" &&
                    (() => {
                      // console.log("levelSkillReport", levelSkillReport)
                      const selectedLevelData = levels.find(
                        (l) => l.id === selectedLevel
                      );
                      // console.log("selectedLevelData", selectedLevelData)
                      const topics_dt = selectedLevelData.topics;
                      // console.log("breadcrumbs", breadcrumbs);
                      const levelsIndex = breadcrumbs.findIndex(
                        (crumb) => crumb.path === "/levels"
                      );

                      const updatedBreadcrumbs =
                        levelsIndex !== -1
                          ? breadcrumbs.slice(0, levelsIndex + 1)
                          : breadcrumbs;
                      // console.log("updatedBreadcrumbs", updatedBreadcrumbs)

                      const handleNnavigate = (id, name) => {
                        // console.log("selectedLevelData", selectedLevelData)
                        const array = [
                          {
                            id: selectedLevelData.id,
                            title: selectedLevelData.name,
                            path: "/topics",
                          },
                          {
                            id: id,
                            title: name,
                            path: "/subtopics",
                          },
                          {
                            id: undefined,
                            title: "Overall Reports",
                            path: "/overall-report",
                          },
                          {
                            id: undefined,
                            title: "Reports",
                            path: "/reports",
                          },
                        ];
                        const mergedBreadcrumbs = [
                          ...updatedBreadcrumbs,
                          ...array,
                        ];
                        console.log("mergedBreadcrumbs", mergedBreadcrumbs);
                        setBreadcrumbs(mergedBreadcrumbs);
                        navigate("/reports");
                      };

                      return (
                        <div className="graph-wrapper">
                          <div className="subtopic-cards">
                            {topics_dt.map((topic, index) => {
                              const isSVG = topic.thumbnail.endsWith(".svg");
                              return (
                                <div
                                  className="topic-card"
                                  key={index}
                                  onClick={() =>
                                    handleNnavigate(topic.id, topic.name)
                                  }
                                >
                                  {isSVG ? (
                                    <InlineSVG
                                      url={
                                        process.env.REACT_APP_PUBLIC_API_URL +
                                        topic.thumbnail
                                      }
                                      topicsName={`topic-${index}`}
                                    />
                                  ) : (
                                    <img
                                      src={
                                        process.env.REACT_APP_PUBLIC_API_URL +
                                        topic.thumbnail
                                      }
                                      alt={
                                        process.env.REACT_APP_PUBLIC_API_URL +
                                        topic.thumbnail
                                      }
                                      className="topics-object fallback-background"
                                      style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                      }}
                                    />
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}

                  <div className="level-nav-wrapper">
                    <button
                      className="level-nav-button level-btn-back"
                      onClick={handlePrevLevel}
                      disabled={!navState.canPrev}
                    >
                      Previous Level
                    </button>
                    <button
                      className="level-nav-button level-btn-forward"
                      onClick={handleNextLevel}
                      disabled={!navState.canNext}
                    >
                      Next Level
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        {primarySkillModal && (
          <div
            className="graph-overlay"
            onClick={() => setPrimarySkillModal(false)}
          >
            <div
              className="c-graph-modal-box"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="graph-close-btn"
                onClick={() => setPrimarySkillModal(false)}
              >
                ×
              </button>

              <div className="modal-header">
                <h2>Your Primary skill in each level</h2>
              </div>

              <div className="consistency-chart">
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart
                    data={filteredSkillReport.map((item, idx) => ({
                      ...item,
                      barColor:
                        skillColorPalette[idx % skillColorPalette.length],
                    }))}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="level" />
                    <YAxis domain={[0, 100]} />
                    <Tooltip
                      cursor={false}
                      position={{ y: 0 }}
                      contentStyle={{
                        backgroundColor: "#ffffff",
                        borderRadius: "8px",
                        border: "1px solid #e5e7eb",
                        fontSize: "14px",
                        padding: "12px",
                      }}
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <div
                              style={{
                                backgroundColor: "#ffffff",
                                borderRadius: "8px",
                                border: "1px solid #e5e7eb",
                                padding: "12px",
                                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                              }}
                            >
                              <p
                                style={{
                                  margin: "0 0 6px 0",
                                  fontSize: "16px",
                                  fontWeight: 600,
                                  color: "#666",
                                }}
                              >
                                Skills:
                              </p>
                              <div style={{ paddingLeft: "8px" }}>
                                {data.skills &&
                                  data.skills.map((skill, idx) => (
                                    <p
                                      key={idx}
                                      style={{
                                        margin: "4px 0",
                                        fontSize: "15px",
                                        color: "#555",
                                      }}
                                    >
                                      • {skill}
                                    </p>
                                  ))}
                              </div>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Bar
                      dataKey="highest_skill"
                      radius={[12, 12, 0, 0]}
                      barSize={38}
                      shape={<CustomBarPrimary colorKey="barColor" />}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="primary-skill-legend">
                <h4 className="legend-title">Skills Highlighted</h4>

                <div className="legend-items">
                  {primarySkillLegend.map((skill, idx) => (
                    <div key={idx} className="c-legend-item">
                      <span
                        className="legend-color-dot"
                        style={{
                          backgroundColor:
                            skillColorPalette[idx % skillColorPalette.length],
                        }}
                      ></span>
                      <span className="legend-text">{skill}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {strengthModal && (
          <div
            className="graph-overlay"
            onClick={() => setStrengthModal(false)}
          >
            <div
              className="c-graph-modal-box"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="graph-close-btn"
                onClick={() => setStrengthModal(false)}
              >
                ×
              </button>

              <div className="modal-header">
                <h2>Top Strengths</h2>
              </div>

              <div className="skill-list-modal">
                {topStrengthSkills.length > 0 ? (
                  topStrengthSkills.map((skill, idx) => (
                    <div key={idx} className="skill-row">
                      <span className="skill-name">{skill.skill}</span>
                      <span className="skill-score">{skill.overall}/100</span>
                    </div>
                  ))
                ) : (
                  <p>No strong skills identified yet.</p>
                )}
              </div>
            </div>
          </div>
        )}

        {improveModal && (
          <div className="graph-overlay" onClick={() => setImproveModal(false)}>
            <div
              className="c-graph-modal-box"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="graph-close-btn"
                onClick={() => setImproveModal(false)}
              >
                ×
              </button>

              <div className="modal-header">
                <h2>Areas to Improve</h2>
              </div>

              <div className="skill-list-modal">
                {improvementSkills.length > 0 ? (
                  improvementSkills.map((skill, idx) => (
                    <div key={idx} className="skill-row">
                      <span className="skill-name">{skill.skill}</span>
                      <span className="skill-score">{skill.overall}/100</span>
                    </div>
                  ))
                ) : (
                  <p>No improvement areas identified</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default SkillReportPage;
